import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { PlanName } from '../../../api/types';
import { useAuth } from '../../../app/providers/useAuth';
import { Button } from '../../../shared/ui/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CurrentPlanCard } from '../components/CurrentPlanCard';
import { PaymentStatusBanner, type PaymentBannerKind } from '../components/PaymentStatusBanner';
import { PendingInvoiceNotice } from '../components/PendingInvoiceNotice';
import { PlanCard } from '../components/PlanCard';
import {
  useActivationPolling,
  type ActivationPhase,
  type ActivationStartPhase,
} from '../hooks/useActivationPolling';
import { useBillingPlans } from '../hooks/useBillingPlans';
import { useBillingStatus } from '../hooks/useBillingStatus';
import { useCancelSubscription } from '../hooks/useCancelSubscription';
import {
  useStartSubscription,
  type SubscriptionErrorKind,
  type SubscriptionIntent,
} from '../hooks/useStartSubscription';
import {
  formatBillingDate,
  hasPendingInvoice,
  isDowngrade,
  resolveBillingUiState,
} from '../lib/billingState';
import styles from './BillingScreen.module.css';

type DialogState =
  { kind: 'cancel' } | { kind: 'resume'; plan: PlanName } | { kind: 'changePlan'; plan: PlanName };

type NoticeKind = 'alreadyActive' | 'resumed';

interface BillingScreenProps {
  /** PARA-140 (AC-4): переход на экран истории платежей (/billing/history). */
  onOpenTransactionHistory: () => void;
}

// PARA-131 — экран подписки: состояние, прайс, оплата, отмена, возобновление,
// смена тарифа. Тариф в UI меняется ТОЛЬКО по ответу GET /billing/me (AC-11):
// queryClient.setQueryData для ['billing','me'] не используется нигде.
export function BillingScreen({ onOpenTransactionHistory }: BillingScreenProps): JSX.Element {
  const { t, i18n } = useTranslation('billing');
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [notice, setNotice] = useState<NoticeKind | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const handleActivated = useCallback(() => {
    // AC-6: снятие лимита меняет поведение экрана задач.
    void queryClient.invalidateQueries({ queryKey: ['tasks', user?.id] });
  }, [queryClient, user?.id]);

  const polling = useActivationPolling(handleActivated);
  const statusQuery = useBillingStatus(polling.refetchInterval);
  const plansQuery = useBillingPlans();

  const { observe, snapshot, start: startWaiting, reset: resetWaiting } = polling;
  const statusData = statusQuery.data;
  useEffect(() => observe(statusData), [observe, statusData]);

  const handleAlreadyActive = useCallback(
    (_plan: PlanName, intent: SubscriptionIntent) => {
      // AC-5a: пользователь ничего не платил — «оплата принята» здесь ложь.
      resetWaiting();
      setNotice(intent === 'resume' ? 'resumed' : 'alreadyActive');
    },
    [resetWaiting],
  );

  const status = statusQuery.data;
  const cancellation = useCancelSubscription();
  const { reset: resetCancellation } = cancellation;

  const handleActivationWait = useCallback(
    (plan: PlanName, phase: ActivationStartPhase) => {
      startWaiting(plan, phase);
      // AC-6: первый опрос статуса уходит немедленно, а не через
      // POLLING_INTERVAL_MS — активация по webhook'у могла опередить колбэк.
      void queryClient.invalidateQueries({ queryKey: ['billing', 'me', user?.id] });
    },
    [queryClient, startWaiting, user?.id],
  );

  const prepareSubscription = useCallback(() => {
    setNotice(null);
    setDialog(null);
    resetCancellation();
    // AC-10: срок снимается ДО отправки POST — иначе поллинг принял бы
    // текущую дату за новую и завершился бы «успехом» мгновенно. Хук зовёт
    // подготовку и на «Попробовать снова», поэтому снимок всегда свежий.
    snapshot(status?.expires_at ?? null);
  }, [resetCancellation, snapshot, status?.expires_at]);

  const subscription = useStartSubscription({
    onBeforeStart: prepareSubscription,
    onActivationWait: handleActivationWait,
    onAlreadyActive: handleAlreadyActive,
  });

  const uiState = status ? resolveBillingUiState(status) : null;
  const isWaiting = polling.phase === 'paid' || polling.phase === 'pending';
  const isBusy = isWaiting || subscription.isPending || cancellation.isPending;

  const { start: beginSubscription } = subscription;

  const handleSelectPlan = useCallback(
    (plan: PlanName) => {
      // AC-14/AC-14b: смена действующего тарифа отменяет его автопродление —
      // предупреждаем до POST, а не после.
      if (status !== undefined && uiState === 'active-renewing' && plan !== status.plan) {
        setDialog({ kind: 'changePlan', plan });
        return;
      }
      beginSubscription(plan, 'purchase');
    },
    [beginSubscription, status, uiState],
  );

  const { mutate: requestCancellation } = cancellation;

  const handleConfirmDialog = useCallback(() => {
    if (dialog === null) {
      return;
    }
    if (dialog.kind === 'cancel') {
      setDialog(null);
      setNotice(null);
      requestCancellation();
      return;
    }
    beginSubscription(dialog.plan, dialog.kind === 'resume' ? 'resume' : 'purchase');
  }, [beginSubscription, dialog, requestCancellation]);

  const retryBoth = useCallback(() => {
    void statusQuery.refetch();
    void plansQuery.refetch();
  }, [plansQuery, statusQuery]);

  // AC-E2: без статуса показывать нечего — экран ошибки с «Повторить» нужен и
  // тогда, когда прайс загрузился. Условие «упали оба» оставляло отказ
  // GET /billing/me в скелетоне навсегда: ретраи исчерпаны, выхода нет.
  // Оговорка `status === undefined` обязательна: isError истинно и когда упал
  // ФОНОВЫЙ рефетч поверх уже полученных данных (опрос активации,
  // refetchOnWindowFocus при закрытии платёжного шита). Без неё «Оплата
  // принята» подменялась бы тревожным «не удалось загрузить» (AC-6, AC-E3).
  if (statusQuery.isError && status === undefined) {
    return (
      <div className={styles.screen}>
        <p className={styles.message}>{t('errors.load')}</p>
        <div className={styles.messageAction}>
          <Button variant="secondary" fullWidth onClick={retryBoth}>
            {t('errors.retry')}
          </Button>
        </div>
      </div>
    );
  }

  // AC-4: скелетон без «скачка» вёрстки; кнопки оплаты недоступны.
  if (status === undefined || uiState === null) {
    return (
      <div className={styles.screen}>
        <h1 className={styles.title}>{t('title')}</h1>
        <div className={styles.skeleton} data-testid="billing-skeleton" />
      </div>
    );
  }

  const bannerKind = resolveBannerKind(polling.phase, subscription.error?.kind);
  const paidPlans = (plansQuery.data?.plans ?? []).filter((price) => price.plan !== 'FREE');
  const periodDays = plansQuery.data?.period_days ?? 0;

  return (
    <div className={styles.screen}>
      <h1 className={styles.title}>{t('title')}</h1>

      <CurrentPlanCard
        status={status}
        uiState={uiState}
        isBusy={isBusy}
        onCancel={() => setDialog({ kind: 'cancel' })}
        onResume={() => setDialog({ kind: 'resume', plan: status.plan })}
      />

      {bannerKind !== null && (
        <PaymentStatusBanner
          kind={bannerKind}
          targetPlan={polling.plan}
          currentPlan={status.plan}
          currentExpiresAt={status.expires_at}
          onRefresh={() => void statusQuery.refetch()}
          onRetry={subscription.retry}
        />
      )}

      {/* PARA-140 (AC-6a): окно поллинга исчерпано — webhook мог не прийти
          вовсе (H1/H2). Пользователь не остаётся без объяснения: к
          нетревожному «оплата обрабатывается» добавляется подсказка
          обратиться в поддержку. */}
      {bannerKind === 'timeout' && (
        <p className={styles.notice} data-testid="payment-support-hint">
          {t('payment.supportHint')}
        </p>
      )}

      {notice !== null && (
        <p className={styles.notice} data-testid="billing-notice">
          {t(`notices.${notice}`)}
        </p>
      )}

      {subscription.error !== null && subscription.error.kind !== 'paymentFailed' && (
        <p className={styles.message} data-testid="subscription-error">
          {t(subscriptionErrorKey(subscription.error.kind, subscription.error.intent))}
        </p>
      )}

      {cancellation.isError && (
        <p className={styles.message} data-testid="cancel-error">
          {t('errors.cancelUnavailable')}
        </p>
      )}

      {/* AC-13b: баннер стоит непосредственно над прайсом и скрыт во время
          ожидания активации — иначе рядом были бы «оплата принята» и «счёт
          ждёт оплаты». */}
      {hasPendingInvoice(status) && polling.phase === 'idle' && <PendingInvoiceNotice />}

      <section className={styles.plans}>
        <h2 className={styles.plansHeading}>{t('plans.heading')}</h2>
        {/* AC-E2 (частичный успех): статус отрисован, блок тарифов заменён
            сообщением с повтором — а не «наполовину пустым» экраном.
            `data === undefined` — та же идиома, что и у статуса: упавший
            фоновый рефетч не должен противоречить карточкам ниже. */}
        {plansQuery.isError && plansQuery.data === undefined && (
          <>
            <p className={styles.planMessage}>{t('errors.loadPlans')}</p>
            <Button variant="secondary" fullWidth onClick={() => void plansQuery.refetch()}>
              {t('errors.retry')}
            </Button>
          </>
        )}
        {plansQuery.isPending && <p className={styles.planMessage}>{t('plans.loading')}</p>}
        {plansQuery.isSuccess && paidPlans.length === 0 && (
          <p className={styles.planMessage}>{t('plans.empty')}</p>
        )}
        {paidPlans.map((price) => (
          <PlanCard
            key={price.plan}
            plan={price.plan}
            priceStars={price.price_stars}
            periodDays={periodDays}
            isCurrent={price.plan === status.plan}
            // AC-3 + исключение Р-10: у legacy stub-подписки CTA текущего
            // тарифа показывается — иначе продлить доступ было бы нечем.
            showCta={price.plan !== status.plan || uiState === 'active-not-renewing'}
            ctaDisabled={isBusy}
            onSelect={() => handleSelectPlan(price.plan)}
          />
        ))}
      </section>

      {/* PARA-140 (AC-4): точка входа в историю платежей — отдельный экран,
          а не секция биллинга. */}
      <div className={styles.historyLink}>
        <Button
          variant="ghost"
          fullWidth
          data-testid="open-transaction-history"
          onClick={onOpenTransactionHistory}
        >
          {t('history.open')}
        </Button>
      </div>

      {dialog !== null && (
        <ConfirmDialog
          {...buildDialogTexts(dialog, status.plan, status.expires_at, i18n.language, t)}
          onConfirm={handleConfirmDialog}
          onDismiss={() => setDialog(null)}
        />
      )}
    </div>
  );
}

type Translate = ReturnType<typeof useTranslation<'billing'>>['t'];

function resolveBannerKind(
  phase: ActivationPhase,
  errorKind: SubscriptionErrorKind | undefined,
): PaymentBannerKind | null {
  if (errorKind === 'paymentFailed') {
    return 'failed';
  }
  return phase === 'idle' ? null : phase;
}

function subscriptionErrorKey(kind: SubscriptionErrorKind, intent: SubscriptionIntent): string {
  if (kind === 'unavailable') {
    // AC-E9: у возобновления собственная формулировка — оно не «оформление».
    return intent === 'resume' ? 'errors.resumeUnavailable' : 'errors.unavailable';
  }
  return kind === 'invoiceUnsupported' ? 'errors.invoiceUnsupported' : 'errors.contractViolation';
}

/** Тексты трёх диалогов (AC-12 / AC-13c / AC-14 / AC-14b) — все с реальной
 * локализованной датой, а не абстрактным «до конца периода». */
function buildDialogTexts(
  dialog: DialogState,
  currentPlan: PlanName,
  expiresAt: string | null,
  locale: string,
  t: Translate,
): { title: string; message: string; notes: string[] } {
  const date = expiresAt ? formatBillingDate(expiresAt, locale) : '';
  const currentPlanName = t(`plans.${currentPlan}.name`);

  if (dialog.kind === 'cancel') {
    return {
      title: t('dialogs.cancel.title'),
      message: t('dialogs.cancel.text', { plan: currentPlanName, date }),
      notes: [],
    };
  }
  if (dialog.kind === 'resume') {
    return {
      title: t('dialogs.resume.title'),
      message: t('dialogs.resume.text', { date }),
      notes: [],
    };
  }

  const targetPlanName = t(`plans.${dialog.plan}.name`);
  const notes = [t('dialogs.changePlan.noProration', { currentPlan: currentPlanName })];
  if (isDowngrade(currentPlan, dialog.plan)) {
    notes.push(t('dialogs.changePlan.downgrade', { currentPlan: currentPlanName, date }));
  }
  return {
    title: t('dialogs.changePlan.title', { plan: targetPlanName }),
    message: t('dialogs.changePlan.text', {
      plan: targetPlanName,
      currentPlan: currentPlanName,
      date,
    }),
    notes,
  };
}
